import { Component, OnInit, OnDestroy, HostListener, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { getCurrentUser } from 'aws-amplify/auth';
import { ClientService, Todo } from '../../services/client.service';
import { UserService } from '../../services/user.service';
import { Subscription } from 'rxjs';

interface DayCell {
  day: number;
  key: string;      // YYYY-MM-DD
  hasDue: boolean;
  overdue: boolean;
}

/**
 * Top-bar calendar popover: a month view that marks the days with open to-dos due
 * (things still to close) and lists them for the selected day.
 */
@Component({
  selector: 'app-calendar-popover',
  imports: [CommonModule],
  templateUrl: './calendar-popover.html',
})
export class CalendarPopoverComponent implements OnInit, OnDestroy {
  private clientService = inject(ClientService);
  private userService = inject(UserService);
  private router = inject(Router);

  readonly today = new Date();
  readonly todayKey = this.dateKey(this.today);
  readonly weekdays = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

  isOpen = false;
  viewYear = this.today.getFullYear();
  viewMonth = this.today.getMonth(); // 0-based
  cells: (DayCell | null)[] = [];
  selectedKey = this.todayKey;
  overdueCount = 0;

  private dueByDate: Record<string, Todo[]> = {};        // windowed: prev/current/next month of the view
  private allDue: { key: string; todo: Todo }[] = [];     // every open due to-do (fetched once)
  private currentUserId: string | null = null;
  private subs: Subscription[] = [];

  async ngOnInit() {
    this.buildCalendar();
    await this.userService.init();

    // Resolve the DB user id the same way the dashboard/tasks pages do:
    // match the logged-in Cognito id against the users list.
    let cognitoId: string | null = null;
    try {
      cognitoId = (await getCurrentUser()).userId;
    } catch {
      cognitoId = null;
    }

    this.subs.push(
      this.userService.users$.subscribe(users => {
        const me = cognitoId ? users.find(u => u.cognitoId === cognitoId) : null;
        if (me?.id && me.id !== this.currentUserId) {
          this.currentUserId = me.id;
          this.loadDue();
        }
      }),
    );
  }

  ngOnDestroy() {
    this.subs.forEach(s => s.unsubscribe());
  }

  /** Close the popover when clicking anywhere outside this component. */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.isOpen) return;
    const target = event.target as HTMLElement;
    if (!target.closest('app-calendar-popover')) {
      this.isOpen = false;
    }
  }

  private async loadDue() {
    try {
      const links = await this.clientService.getTodosByUserFiltered(this.currentUserId!, { excludeDone: true });
      const all: { key: string; todo: Todo }[] = [];
      let overdue = 0;
      for (const link of links) {
        const todo = link.todo;
        if (!todo?.dueDate) continue;
        const key = String(todo.dueDate).split('T')[0];
        all.push({ key, todo });
        if (key < this.todayKey) overdue++;
      }
      this.allDue = all;
      this.overdueCount = overdue; // honest badge: all overdue, regardless of window
      this.buildCalendar();
    } catch (e) {
      console.error('[Calendar] Failed to load due tasks', e);
    }
  }

  /**
   * Build the due-date map for the visible 3-month window: previous, current and
   * next month of whatever month is in view. Recomputed on each navigation.
   */
  private rebuildWindow() {
    const start = this.dateKey(new Date(this.viewYear, this.viewMonth - 1, 1));   // 1st of prev month
    const end = this.dateKey(new Date(this.viewYear, this.viewMonth + 2, 0));      // last day of next month
    const map: Record<string, Todo[]> = {};
    for (const { key, todo } of this.allDue) {
      if (key >= start && key <= end) (map[key] ??= []).push(todo);
    }
    this.dueByDate = map;
  }

  private dateKey(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  private buildCalendar() {
    this.rebuildWindow(); // keep the due-date map scoped to prev/current/next month
    const first = new Date(this.viewYear, this.viewMonth, 1);
    const daysInMonth = new Date(this.viewYear, this.viewMonth + 1, 0).getDate();
    const lead = (first.getDay() + 6) % 7; // Monday-first
    const cells: (DayCell | null)[] = [];
    for (let i = 0; i < lead; i++) cells.push(null);
    for (let day = 1; day <= daysInMonth; day++) {
      const key = `${this.viewYear}-${String(this.viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const list = this.dueByDate[key] || [];
      cells.push({ day, key, hasDue: list.length > 0, overdue: list.length > 0 && key < this.todayKey });
    }
    this.cells = cells;
  }

  get monthLabel(): string {
    return new Date(this.viewYear, this.viewMonth, 1)
      .toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  }

  get selectedTodos(): Todo[] {
    return this.dueByDate[this.selectedKey] || [];
  }

  get selectedLabel(): string {
    const [y, m, d] = this.selectedKey.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });
  }

  toggle() {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      // Reset to the current month on open.
      this.viewYear = this.today.getFullYear();
      this.viewMonth = this.today.getMonth();
      this.selectedKey = this.todayKey;
      this.buildCalendar();
      // Refresh due tasks each time it's opened.
      if (this.currentUserId) this.loadDue();
    }
  }

  prevMonth() {
    if (this.viewMonth === 0) { this.viewMonth = 11; this.viewYear--; } else { this.viewMonth--; }
    this.buildCalendar();
  }

  nextMonth() {
    if (this.viewMonth === 11) { this.viewMonth = 0; this.viewYear++; } else { this.viewMonth++; }
    this.buildCalendar();
  }

  select(cell: DayCell) {
    this.selectedKey = cell.key;
  }

  isToday(key: string): boolean { return key === this.todayKey; }
  isSelected(key: string): boolean { return key === this.selectedKey; }

  openTodo(t: Todo) {
    this.isOpen = false;
    this.router.navigate(['/tasks'], { queryParams: { todoId: t.id } });
  }
}
